/* cf-alu-two-gates.js - Click Fencing, Blade / Flat Top / PerfMesh calculators - 4 Oct 2026
 *
 * TWO GATES IN ONE SECTION on the aluminium calculators.
 *
 * Pete, 4 Oct 2026: "the aluminium fences need the same thing except instead of a stainless steel
 * post they will get a standard post the same as their selection for installation."
 * The glass version is cf-two-gates.js (3 Oct). Aluminium is simpler in one way: every gate already
 * hangs between two of the fence's own posts (no wall-mount hinge for aluminium, Pete 30 Aug), so a
 * second gate just brings its posts with it - the section's own post, in-ground or base mount as
 * chosen, with its cap and dress ring when in-ground (cf-post-caps-rings.js counts them).
 *
 * WHAT IT ADDS to the Gate panel of every section: "Second gate in this section?", each gate's
 * position (At the left end / At the right end / Measured from an end), Gate 2 Length and Gate 2
 * Hinge Side. The wording is the glass wording Pete approved on 3-4 Oct, reused verbatim.
 * Gate 2 uses the same hinge and latch the customer chose for gate 1 (always Satin Black, Pete 1 Oct).
 *
 * HOW. A section with two gates is solved here: gates where the customer put them (a measured gate
 * moves the least it must, 1mm steps), the runs between them filled with panels the calculator's
 * way (full sheets, then one cut panel of at least 500mm where possible, never under 100mm).
 *   - The calculation every price reads (state.optimizedConfigs, the job cut plan) carries the REAL
 *     panels only, so panels, brackets and kits are billed exactly as for any section.
 *   - The drawing is made by the calculator's own renderer, given the whole run with both gates as
 *     slots, so posts, rake, end posts and the post count come out of its own code. The two gate
 *     slots are then repainted as gates with that calculator's own gate drawing.
 *   - Gate 2's gate, hinges and latch: the calculator's own calculateGateCosts, run a second time.
 *   - Posts: Blade counts the drawing (right already); Flat Top's countRenderedPosts and PerfMesh's
 *     post line get the one extra post. Supply & Install counts the second gate through each
 *     calculator's own formula (surcharges and minimums included).
 *
 * Self-installing, idempotent, every hook in try/catch, aluminium calculator pages only.
 * Wraps the PROTOTYPE at defer time so it is the innermost layer under every other patch.
 */
(function () {
  'use strict';
  var TAG = '[cf-alu-two-gates]';
  var path = (location && location.pathname) || '';
  var KIND = /blade-fence-calculator/.test(path) ? 'blade'
           : /flat-top-fence-calculator/.test(path) ? 'flattop'
           : /perfmesh-fence/.test(path) ? 'perfmesh' : null;
  if (!KIND) return;
  var Cls;
  try { Cls = GlassFencingCalculator; } catch (e) { return; }   // eslint-disable-line no-undef
  if (!Cls || !Cls.prototype) return;
  var P = Cls.prototype;
  if (P.__cfAluTwoGates) return;
  if (typeof P.renderSectionPreview !== 'function' || typeof P.calculateGateCosts !== 'function' ||
      typeof P.calculateSpread !== 'function' || typeof P.calculateJobCutPlan !== 'function') return;
  if (KIND === 'flattop' && typeof P.countRenderedPosts !== 'function') return;

  function warn(m, e) { try { console.warn(TAG + ' ' + m, e && (e.message || e)); } catch (x) {} }
  function num(v) { var n = parseInt(v, 10); return isFinite(n) ? n : 0; }
  var MAX_SHIFT = 1500;
  var MIN_PANEL = 100;     // the smallest cut panel any of the three calculators draws
  // The smallest cut panel each calculator makes on its own: Blade 100, Flat Top and PerfMesh 500.
  // A smaller one (down to 100) is used only when the gates cannot be placed any other way.
  var MIN_NATIVE = KIND === 'blade' ? 100 : 500;
  var GOOD_FILLER = 500;   // Peter's rule: a cut panel of at least 500mm where there is room

  function isCustom(s) { return !!(s && s.hasGate && s.hasGate2 && num(s.length) > 0); }
  function secNum(gc, s) {
    var secs = gc.state && gc.state.sections; if (!secs) return null;
    for (var k in secs) if (secs[k] === s) return parseInt(k, 10);
    return null;
  }

  /* ------------------------------------------------------------ the pure solver */
  // Hinge side: true = hinges drawn on the gate's left edge. Blade and Flat Top: gateSide is the
  // hinge side. PerfMesh: its radio values are swapped in the HTML ('right' = LHS Hinge).
  function g1HingeLeft(s) { return KIND === 'perfmesh' ? s.gateSide === 'right' : s.gateSide === 'left'; }
  function gateSideFor(hingeLeft) { return KIND === 'perfmesh' ? (hingeLeft ? 'right' : 'left') : (hingeLeft ? 'left' : 'right'); }

  function rakeWidth(gc, s) {
    try { if (typeof gc.getRakedPanelWidth === 'function') { var w = num(gc.getRakedPanelWidth(s.fenceHeight)); if (w) return w; } } catch (e) {}
    try { return CONFIG.DEFAULTS.RAKED_PANEL_WIDTH; } catch (e) { return 1200; }   // eslint-disable-line no-undef
  }

  function cfgFor(gc, s) {
    var Pw = num(s.gapSpacing) || 50;
    var ep = s.endPostSetup || 'both';
    var remL = (ep === 'no-left' || ep === 'no-both') ? 1 : 0;
    var remR = (ep === 'no-right' || ep === 'no-both') ? 1 : 0;
    var L = num(s.length);
    var g1 = num(s.gateLength) || 970;
    var g2 = num(s.cfG2Length) || g1;
    // The maximum panel is the customer's panel choice. Even Panels writes its even width into
    // panelLength, so read the panel select first, and never use an even width as the maximum.
    var S = 0, n0 = gc ? secNum(gc, s) : null;
    var sel = n0 != null && document.getElementById('section-' + n0 + '-panel');
    if (sel && num(sel.value) >= 1000) S = num(sel.value);
    if (!S && s.panelMode !== 'even') S = num(s.panelLength);
    if (!S) { try { S = num(gc.getDefaultPanelLength(s.fenceHeight)); } catch (e) {} }
    if (!S && KIND === 'blade') { try { S = num(gc.getPanelLengthForHeight(s.fenceHeight)); } catch (e) {} }
    S = S || 2400;
    return {
      L: L, Lp: L + (remL + remR) * Pw, P: Pw, S: S, remL: remL, remR: remR,
      rake: s.hasRaked ? { side: s.rakedSide === 'right' ? 'right' : 'left', w: rakeWidth(gc, s) } : null,
      gates: [
        { num: 1, w: g1, place: s.cfG1Place || 'left', from: s.cfG1From || 'left', mm: s.cfG1Mm, hingeLeft: g1HingeLeft(s) },
        { num: 2, w: g2, place: s.cfG2Place || 'right', from: s.cfG2From || 'right', mm: s.cfG2Mm, hingeLeft: s.cfG2Side ? s.cfG2Side === 'left' : false }
      ],
      last: s.cfLastMoved === 1 || s.cfLastMoved === 2 ? s.cfLastMoved : 0
    };
  }

  // A run of R mm between two posts, filled the calculator's way.
  function fill(R, Pw, S, minP) {
    minP = minP || MIN_PANEL;
    if (R < minP) return null;
    var k = Math.ceil((R + Pw) / (S + Pw));
    var w = [];
    for (var i = 0; i < k - 1; i++) w.push(S);
    var last = R - (k - 1) * (S + Pw);
    if (last < minP && k === 1) return null;
    if (k > 1 && last < GOOD_FILLER) {               // take from the last full sheet
      var pair = S + last;
      w[k - 2] = pair - GOOD_FILLER; last = GOOD_FILLER;
      if (w[k - 2] < minP) return null;
    }
    w.push(last);
    return w;
  }

  // Desired leaf start of each gate (in the "all posts present" frame: 0 = outer face of the left post).
  function startX(cfg, g) {
    var s0 = cfg.rake && cfg.rake.side === 'left' ? cfg.P + cfg.rake.w : 0;
    var e = cfg.Lp - (cfg.rake && cfg.rake.side === 'right' ? cfg.P + cfg.rake.w : 0);
    if (g.place === 'left') return s0 + cfg.P;
    if (g.place === 'right') return e - cfg.P - g.w;
    var mm = num(g.mm);
    return g.from === 'right' ? cfg.Lp - cfg.remR * cfg.P - mm - g.w : cfg.remL * cfg.P + mm;
  }

  // Lay out the section for leaf starts xs. slack: let one too-small gap sit in a gate opening.
  function build(cfg, xs, slack, minP) {
    minP = minP || MIN_PANEL;
    var Pw = cfg.P;
    var s0 = cfg.rake && cfg.rake.side === 'left' ? Pw + cfg.rake.w : 0;
    var e = cfg.Lp - (cfg.rake && cfg.rake.side === 'right' ? Pw + cfg.rake.w : 0);
    var gs = cfg.gates.map(function (g, i) { return { g: g, x: xs[i] }; }).sort(function (a, b) { return a.x - b.x; });
    var seq = [], panels = [], edge = s0, used = 0;
    for (var i = 0; i <= gs.length; i++) {
      var to = i < gs.length ? gs[i].x : e;
      var D = to - edge;
      if (D < Pw) return null;
      var extra = 0;
      if (D > Pw) {
        var R = D - 2 * Pw;
        var w = R >= minP ? fill(R, Pw, cfg.S, minP) : null;
        if (!w) {
          if (!slack || used || D - Pw >= Pw + MIN_PANEL) return null;   // at most a post + 99mm, as the calculators' own gate-only rule
          extra = D - Pw; used = 1;                  // goes into the neighbouring gate opening
        } else {
          w.forEach(function (x) { seq.push({ t: 'panel', w: x }); panels.push(x); });
        }
      }
      if (extra) {
        var host = i > 0 ? seq[seq.length - 1] : null;   // the gate before this gap, else the one after
        if (host && host.t === 'gate') host.slot += extra; else if (i < gs.length) gs[i].extra = extra; else return null;
      }
      if (i < gs.length) {
        var G = gs[i];
        seq.push({ t: 'gate', num: G.g.num, w: G.g.w, slot: G.g.w + (G.extra || 0), hingeLeft: G.g.hingeLeft });
        edge = G.x + G.g.w;
      }
    }
    return { seq: seq, panels: panels };
  }

  function minLength(cfg) {   // every component packed, one post between each
    return cfg.gates[0].w + cfg.gates[1].w + (cfg.rake ? cfg.rake.w : 0) + (cfg.gates.length + (cfg.rake ? 1 : 0) + 1) * cfg.P - (cfg.remL + cfg.remR) * cfg.P;
  }

  function solve(cfg) {
    var g = cfg.gates, error = null;
    if (g[0].place !== 'measured' && g[0].place === g[1].place) error = 'same-end';
    // A gate set at the raked end sits against the rake (Pete, 4 Oct 2026, screenshot).
    var xs = g.map(function (x) { return startX(cfg, x); });
    var mov = g.map(function (x) { return x.place === 'measured'; });
    var out = null, shifts = [0, 0];
    // Every gap between solid things is either one shared post, or a run of at least one panel.
    var Pw = cfg.P, s0 = cfg.rake && cfg.rake.side === 'left' ? Pw + cfg.rake.w : 0;
    var e = cfg.Lp - (cfg.rake && cfg.rake.side === 'right' ? Pw + cfg.rake.w : 0);
    var w0 = g[0].w, w1 = g[1].w, RUN;
    var okD = function (D) { return D === Pw || D >= RUN; };
    var ok = function (a, b) {
      if (a > b) { var t0 = a; a = b; b = t0; var wa = w1, wb = w0; } else { wa = w0; wb = w1; }
      return okD(a - s0) && okD(b - a - wa) && okD(e - b - wb);
    };
    var okXY = function (x0, x1) { return x0 <= x1 ? ok(x0, x1) : (okD(x1 - s0) && okD(x0 - x1 - w1) && okD(e - x0 - w0)); };
    var passes = MIN_NATIVE > MIN_PANEL ? [MIN_NATIVE, MIN_PANEL] : [MIN_PANEL];
    for (var pi = 0; pi < passes.length && !out && !error; pi++) {
      RUN = 2 * Pw + passes[pi];
      var found = null;
      // A measured gate stays close to where it was put: the calculator's own panel sizes are
      // preferred only within 250mm of it; past that the gate stays put and a smaller panel is cut.
      var maxT = (pi === 0 && passes.length > 1) ? 250 : MAX_SHIFT;
      for (var t = 0; t <= maxT && !found; t++) {
        if (mov[0] && mov[1] && cfg.last && !pi && t === 0) {
          // The gate the customer just placed stays put if the other one can make room.
          var keep = cfg.last - 1, other = 1 - keep;
          for (var t2 = 0; t2 <= MAX_SHIFT && !found; t2++) {
            for (var sg = 1; sg >= -1 && !found; sg -= 2) {
              var xx = xs.slice(); xx[other] += sg * t2;
              if (okXY(xx[0], xx[1])) { found = [0, 0]; found[other] = sg * t2; }
            }
          }
          if (found) break;
        }
        if (mov[0] && mov[1]) {
          for (var a = -t; a <= t && !found; a++) {
            var b = t - Math.abs(a);
            if (okXY(xs[0] + a, xs[1] + b)) found = [a, b];
            else if (b && okXY(xs[0] + a, xs[1] - b)) found = [a, -b];
          }
        } else if (mov[0] || mov[1]) {
          var k = mov[0] ? 0 : 1;
          for (var sgn = 1; sgn >= -1 && !found; sgn -= 2) {
            var d = sgn * t; var x2 = xs.slice(); x2[k] += d;
            if (okXY(x2[0], x2[1])) { found = [0, 0]; found[k] = d; }
          }
        } else { if (okXY(xs[0], xs[1])) found = [0, 0]; break; }
      }
      if (found) { out = build(cfg, [xs[0] + found[0], xs[1] + found[1]], false, passes[pi]); shifts = found; }
    }
    if (!error) {
      if (!out) out = build(cfg, xs, true, MIN_NATIVE) || build(cfg, xs, true);
      if (!out) error = 'no-fit';
    }
    var compact = false;
    if (!out) {    // never quote something that is not drawn: fall back to the tidiest layout
      var c = JSON.parse(JSON.stringify(cfg));
      var ends = ['left', 'right'];
      if (cfg.rake) ends = cfg.rake.side === 'left' ? ['right', 'measured'] : ['left', 'measured'];
      c.gates[0].place = ends[0]; c.gates[1].place = ends[1];
      if (ends[1] === 'measured') { c.gates[1].from = cfg.rake.side; c.gates[1].mm = cfg.rake.w + 2 * cfg.P; }
      var cx = c.gates.map(function (x) { return startX(c, x); });
      out = build(c, cx, true, MIN_NATIVE) || build(c, cx, true, MIN_PANEL);
      compact = true;
    }
    if (!out) return { error: error || 'no-fit', tooShort: cfg.L < minLength(cfg), minLength: minLength(cfg) };
    return { seq: out.seq, panels: out.panels, error: error, compact: compact,
             moved: shifts.map(function (d, i) { return { num: i + 1, shift: d }; }).filter(function (m) { return m.shift; }) };
  }

  var CF_ALU2G = { solve: solve, fill: fill, minLength: minLength, build: build, startX: startX };
  try { window.CF_ALU2G = CF_ALU2G; } catch (e) {}

  /* ------------------------------------------------------------ layout cache */
  function solveFor(gc, s, ep) {
    var save = s.endPostSetup; s.endPostSetup = ep;
    var cfg = cfgFor(gc, s); s.endPostSetup = save;
    cfg.rake && (cfg.rake.w = rakeWidth(gc, s));
    var lay = solve(cfg); lay.cfg = cfg;
    return lay;
  }
  function setEp(gc, s, ep) {
    if (s.endPostSetup === ep) return;
    s.endPostSetup = ep;
    try { var n1 = secNum(gc, s), es = n1 != null && document.getElementById('section-' + n1 + '-end-post-setup'); if (es) es.value = ep; } catch (e) {}
  }
  function layoutFor(gc, s) {
    // The customer's own Side End Post Setup (if they changed the select since we last set it, theirs wins).
    var chosen = (s.cfEpApplied && s.endPostSetup === s.cfEpApplied) ? (s.cfEpChosen || 'both') : (s.endPostSetup || 'both');
    var save = s.endPostSetup; s.endPostSetup = chosen;
    var key = JSON.stringify(cfgFor(gc, s)) + '|' + rakeWidth(gc, s);
    s.endPostSetup = save;
    if (s.__cfA2gKey === key && s.__cfA2gLay) { setEp(gc, s, s.__cfA2gLay.ep); return s.__cfA2gLay; }
    // Side End Post Setup is the customer's choice: "no post" means no post, even beside a gate
    // (Pete, 4 Oct 2026: "if the customer choses no post, the post should be removed.").
    var lay = solveFor(gc, s, chosen), final = chosen;
    // Still nothing: lengthen to the first length that builds, as for a section that is too short.
    if (!lay.seq) {
      var c0 = lay.cfg, L0 = c0 ? c0.L : num(s.length), fit = 0;
      for (var dL = 1; dL <= 3000 && !fit; dL++) {
        var t2 = {}; for (var k2 in c0) t2[k2] = c0[k2];
        t2.L = L0 + dL; t2.Lp = c0.Lp + dL;
        if (solve(t2).seq) fit = t2.L;
      }
      lay.tooShort = true; lay.minLength = fit || Math.max(L0 + 1, lay.minLength || 0);
    }
    lay.ep = final;
    if (final !== chosen) { s.cfEpChosen = chosen; s.cfEpApplied = final; } else { s.cfEpChosen = null; s.cfEpApplied = null; }
    setEp(gc, s, final);
    try { Object.defineProperty(s, '__cfA2gLay', { value: lay, enumerable: false, writable: true, configurable: true });
          Object.defineProperty(s, '__cfA2gKey', { value: key, enumerable: false, writable: true, configurable: true }); }
    catch (e) { s.__cfA2gKey = key; s.__cfA2gLay = lay; }
    return lay;
  }

  /* ------------------------------------------------------------ calculation hooks */
  var RENDER = null;          // { n, lay } while the drawing is being made
  var BYPASS = 0;

  // Which section a solver call is for. While a section's own calculation runs (CUR) that section
  // decides. Otherwise a two-gate section is matched by length + gate leaf only when no OTHER gated
  // section shares them - a single-gate section of the same length must never get two gates' panels
  // (Blade break test 4 Oct: 4000 single + 4000 two-gate under-quoted the single by $411).
  var CUR = null;
  function sectionForSpread(gc, len, element, gateLeaf) {
    if (!/gate/.test(String(element || ''))) return null;
    if (CUR) return isCustom(CUR) ? CUR : null;
    var secs = gc.state && gc.state.sections, hit = null, count = 0, others = 0;
    if (!secs) return null;
    for (var k in secs) {
      var s = secs[k];
      if (!s || !s.hasGate || !num(s.length)) continue;
      var c = cfgFor(gc, s);
      if (c.Lp !== num(len) || (gateLeaf && num(gateLeaf) !== c.gates[0].w)) continue;
      if (isCustom(s)) { hit = s; count++; } else others++;
    }
    return count === 1 && !others ? hit : null;
  }
  function withCur(s, fn) { var prev = CUR; CUR = s || null; try { return fn(); } finally { CUR = prev; } }

  function realCalc(native, lay) {
    var r = {};
    for (var k in native) r[k] = native[k];
    var pw = lay.panels.slice();
    var S = lay.cfg.S;
    r.valid = true;
    r.panelWidths = pw;
    r.panel_count = pw.filter(function (w) { return w === S; }).length;
    r.numPanels = pw.length; r.regularPanels = pw.length; r.numVariablePanels = r.panel_count;
    r.remaining = 0; r.total = lay.cfg.Lp;
    r.gatePanels = 2; r.hingePanels = 2;
    var posts = pw.length + 2 + (lay.cfg.rake ? 1 : 0) + 1;
    r.totalGaps = posts; r.gapSpacings = Array(posts).fill(lay.cfg.P); r.totalGapWidth = posts * lay.cfg.P;
    if (native.perfomesh) {
      var pm = {}; for (var k2 in native.perfomesh) pm[k2] = native.perfomesh[k2];
      pm.panels = pw.map(function (w) { return { footprint: w, sheet: w, is_cut: w !== S, kit: w === S ? S + 'mm' : 'filler' }; });
      pm.panel_count = r.panel_count; pm.checksOut = true;
      r.perfomesh = pm;
    }
    r.cfTwoGates = true;
    return r;
  }

  var oSpread = P.calculateSpread;
  P.calculateSpread = function (len, pw, sheet, element, isW2P, wallGateEnd, gateLeaf) {
    var r = oSpread.apply(this, arguments);
    if (BYPASS) return r;
    try {
      var s = sectionForSpread(this, len, element, gateLeaf);
      if (s && r) { var lay = layoutFor(this, s); if (lay.seq) return realCalc(r, lay); }
    } catch (e) { warn('spread', e); }
    return r;
  };
  if (typeof P.calculateEvenPanels === 'function') {
    var oEven = P.calculateEvenPanels;
    P.calculateEvenPanels = function (len, pw, sheet, element, gateLeaf) {
      var r = oEven.apply(this, arguments);
      try {
        var s = sectionForSpread(this, len, element, gateLeaf);
        if (s && r) { var lay = layoutFor(this, s); if (lay.seq) { var c = realCalc(r, lay); c.isEvenMode = false; return c; } }
      } catch (e) { warn('even', e); }
      return r;
    };
  }

  var oJob = P.calculateJobCutPlan;
  P.calculateJobCutPlan = function () { return fixPlan(this, oJob.apply(this, arguments)); };
  function fixPlan(gc, plan) {
    if (!plan || typeof plan !== 'object') return plan;
    try {
      var secs = gc.state.sections;
      for (var k in secs) {
        var s = secs[k];
        var n = parseInt(k, 10);
        if (RENDER && RENDER.n === n) {
          // The drawing: the whole run, gates as slots, so the renderer draws every post.
          var S = RENDER.lay.cfg.S;
          plan[n] = { valid: true, cut_panel: null, off_cut: 0, source_sheet: null, order_notes: '',
            panels: RENDER.lay.seq.map(function (it) {
              var w = it.t === 'gate' ? it.slot : it.w;
              return { footprint: w, sheet: w, is_cut: false, kit: w + 'mm', cfGate: it.t === 'gate' ? it.num : 0 };
            }) };
        } else if (isCustom(s)) {   // always the two-gate panels, whatever another layer worked out
          var lay = layoutFor(gc, s);
          if (lay.seq) plan[n] = { valid: true, cut_panel: null, off_cut: 0, source_sheet: null, order_notes: '',
            panels: lay.panels.map(function (w) { return { footprint: w, sheet: w, is_cut: w !== lay.cfg.S, kit: w === lay.cfg.S ? w + 'mm' : 'filler' }; }) };
        }
      }
    } catch (e) { warn('jobplan', e); }
    return plan;
  }

  if (KIND === 'flattop') {
    var oCount = P.countRenderedPosts;
    P.countRenderedPosts = function (n) {
      var r = oCount.apply(this, arguments);
      try { var s = this.state.sections[n]; if (isCustom(s) && typeof r === 'number' && layoutFor(this, s).seq) r += 1; } catch (e) {}
      return r;
    };
  }

  if (KIND === 'perfmesh' && typeof P.calculatePanelCosts === 'function') {
    // PerfMesh counts posts inside calculatePanelCosts as panels + 1 + one gate: add the second gate's post.
    var oPanels = P.calculatePanelCosts;
    P.calculatePanelCosts = function (section) {
      var bd = this.state.quoteBreakdown || [], start = bd.length;
      var r = oPanels.apply(this, arguments);
      try {
        if (isCustom(section) && layoutFor(this, section).seq) {
          var bd2 = this.state.quoteBreakdown || [];
          for (var i = bd2.length - 1; i >= start; i--) {
            var l = bd2[i];
            if (l && /^(In-Ground|Base Mount) Post \(50mm\)$/.test(l.item || '')) {
              var u = Number(l.unitPrice) || 0;
              l.quantity = (Number(l.quantity) || 0) + 1;
              l.totalPrice = (Number(l.totalPrice) || 0) + u;
              if (typeof r === 'number') r += u;
              break;
            }
          }
        }
      } catch (e) { warn('posts', e); }
      return r;
    };
  }

  // Gate 2: the calculator's own gate, hinge and latch lines, a second time, at gate 2's length.
  var oGate = P.calculateGateCosts, IN_G2 = 0;
  P.calculateGateCosts = function (section) {
    var r = oGate.apply(this, arguments);
    if (IN_G2 || !isCustom(section)) return r;
    try {
      if (!layoutFor(this, section).seq) return r;
      var save = section.gateLength;
      section.gateLength = String(num(section.cfG2Length) || num(save));
      IN_G2 = 1;
      var r2;
      try { r2 = oGate.apply(this, arguments); } finally { section.gateLength = save; IN_G2 = 0; }
      if (typeof r === 'number' && typeof r2 === 'number') r += r2;
    } catch (e) { warn('gate2', e); }
    return r;
  };

  // Supply & Install: each calculator counts gates as "sections with a gate". While its own quote
  // runs, a two-gate section is counted twice, so its own rates, short-run loadings and minimums apply.
  var oUQD = P.updateQuoteDisplay;
  P.updateQuoteDisplay = function () {
    var secs, extra;
    try {
      secs = Object.values(this.state.sections || {});
      extra = secs.filter(function (s) { return isCustom(s) && s.__cfA2gLay && s.__cfA2gLay.seq; });
    } catch (e) { extra = []; }
    if (!extra.length) { var r0 = oUQD.apply(this, arguments); dropGateFee(this); return r0; }
    var of = Array.prototype.filter;
    Array.prototype.filter = function (fn, thisArg) {   // eslint-disable-line no-extend-native
      var res = of.call(this, fn, thisArg);
      try {
        if (this.length === secs.length && typeof fn === 'function' && /hasGate/.test(String(fn))) {
          var same = true;
          for (var i = 0; i < secs.length; i++) if (this[i] !== secs[i]) { same = false; break; }
          if (same) for (var j = 0; j < extra.length; j++) if (fn.call(thisArg, extra[j])) res.push(extra[j]);
        }
      } catch (e) {}
      return res;
    };
    var r1;
    try { r1 = oUQD.apply(this, arguments); } finally { Array.prototype.filter = of; }
    dropGateFee(this);
    return r1;
  };

  // Blade added a hidden $92.16 "processing fee" to its on-screen DIY total whenever a gate was
  // included. It was never a line, so the quote page and the cart never charged it and the totals
  // disagreed. Pete, 4 Oct 2026: "i dont want a gate fee i do want the totals to be right tho".
  function dropGateFee(gc) {
    if (KIND !== 'blade') return;
    try {
      if (gc.state.pricingType === 'install') return;
      var anyGate = Object.values(gc.state.sections || {}).some(function (sec) { return sec && sec.length && sec.hasGate; });
      if (!anyGate || !(gc.state.quote > 92.16)) return;
      gc.state.quote = Math.round((gc.state.quote - 92.16) * 100) / 100;
      gc.updateQuoteElements(gc.state.quote);
    } catch (e) { warn('gate fee', e); }
  }

  /* ------------------------------------------------------------ the drawing */
  var NS = 'http://www.w3.org/2000/svg';
  function mk(tag, a) { var e = document.createElementNS(NS, tag); for (var k in a) e.setAttribute(k, a[k]); return e; }

  function drawGate(svg, before, gx, pw, top, H, hingeOnRight) {
    var add = function (tag, a) { svg.insertBefore(mk(tag, a), before); };
    var bot = top + H;
    if (KIND === 'perfmesh') {
      var inset = 4;
      add('rect', { x: gx + inset, y: top, width: pw - inset * 2, height: H, fill: '#c8c8c8', stroke: '#1a1a1a', 'stroke-width': 2 });
      var hx = hingeOnRight ? gx + pw - inset - 10 : gx + inset + 2;
      [0.22, 0.58].forEach(function (p) { add('rect', { x: hx, y: top + H * p, width: 8, height: H * 0.14, fill: '#1a1a1a', rx: 1 }); });
      add('rect', { x: hingeOnRight ? gx + inset + 2 : gx + pw - inset - 10, y: top + H * 0.38, width: 8, height: H * 0.22, fill: '#555', rx: 1 });
      return;
    }
    if (KIND === 'blade') add('rect', { x: gx - 3, y: top - 5, width: pw + 6, height: H + 10, fill: 'rgba(14,158,213,0.16)', stroke: '#0e9ed5', 'stroke-width': 1.4, rx: 3 });
    var r1 = KIND === 'blade' ? top + 6 : top, r2 = KIND === 'blade' ? bot - 5 - 6 : bot - 5;
    add('rect', { x: gx, y: r1, width: pw, height: 5, fill: '#000' });
    add('rect', { x: gx, y: r2, width: pw, height: 5, fill: '#000' });
    add('rect', { x: gx, y: top, width: 4, height: H, fill: '#000' });
    add('rect', { x: gx + pw - 4, y: top, width: 4, height: H, fill: '#000' });
    var innerW = pw - 8, count = Math.max(4, Math.round(innerW / 13)), sp = innerW / (count + 1);
    for (var i = 1; i <= count; i++) add('rect', { x: gx + 4 + i * sp - 1.5, y: top, width: 3, height: H, fill: '#000' });
    if (KIND === 'blade') {
      var hX = hingeOnRight ? gx + pw - 8 + 2 : gx - 2;
      [0.22, 0.68].forEach(function (f) {
        add('rect', { x: hX, y: top + H * f, width: 8, height: 10, fill: '#444444', stroke: '#222', 'stroke-width': 0.5, rx: 1 });
        add('circle', { cx: hingeOnRight ? gx + pw - 2 : gx + 2, cy: top + H * f + 5, r: 1.5, fill: '#888' });
      });
      var lX = hingeOnRight ? gx - 7 + 2 : gx + pw - 2;
      add('rect', { x: lX, y: top + H * 0.42, width: 7, height: 18, fill: '#bbbbbb', stroke: '#999', 'stroke-width': 0.5, rx: 2 });
      add('rect', { x: hingeOnRight ? lX - 3 : lX + 7, y: top + H * 0.42 + 5.4, width: 3, height: 7.2, fill: '#999', rx: 1 });
    } else {
      var hx2 = hingeOnRight ? gx + pw - 4 - 10 : gx + 4 + 2;
      [0.22, 0.58].forEach(function (p) { add('rect', { x: hx2, y: top + H * p, width: 8, height: H * 0.14, fill: '#1a1a1a', stroke: '#e8e8e8', 'stroke-width': 0.75, rx: 1 }); });
      add('rect', { x: hingeOnRight ? gx + 4 + 2 : gx + pw - 4 - 10, y: top + H * 0.38, width: 8, height: H * 0.22, fill: '#555', stroke: '#e8e8e8', 'stroke-width': 0.75, rx: 1 });
    }
  }

  function bbox(e) { try { return e.getBBox(); } catch (x) { return null; } }

  // Turn the two gate slots the renderer drew as panels into gates.
  function repaint(gc, n, lay) {
    var host = document.getElementById('section-preview');
    var svg = host && host.querySelector('svg');
    if (!svg) return false;
    var kids = Array.prototype.slice.call(svg.children);
    var rects = kids.filter(function (e) { return e.tagName === 'rect'; });
    var posts = rects.filter(function (e) { return e.getAttribute('fill') === '#1a1a1a' && num(e.getAttribute('width')) === 8; })
      .map(function (e) { return { x: parseFloat(e.getAttribute('x')), y: parseFloat(e.getAttribute('y')), h: parseFloat(e.getAttribute('height')) }; })
      .filter(function (p) { return p.h > 40; })
      .sort(function (a, b) { return a.x - b.x; });
    if (posts.length < 2) return false;
    var top = Math.min.apply(null, posts.map(function (p) { return p.y; }));
    var texts = kids.filter(function (e) { return e.tagName === 'text'; });
    var gatesDone = 0;
    // Components in drawing order: rake (left), the slots, rake (right).
    var comps = [];
    if (lay.cfg.rake && lay.cfg.rake.side === 'left') comps.push({ t: 'rake' });
    lay.seq.forEach(function (it) { comps.push(it); });
    if (lay.cfg.rake && lay.cfg.rake.side === 'right') comps.push({ t: 'rake' });
    // Columns = the spaces between consecutive posts that are wider than a post.
    var cols = [];
    for (var i = 0; i < posts.length - 1; i++) {
      var Lb = posts[i].x + 8, Rb = posts[i + 1].x;
      if (Rb - Lb > 2) cols.push({ Lb: Lb, Rb: Rb });
    }
    // An end without a post (Side End Post Setup): its column runs from the drawing's own edge.
    if (cols.length < comps.length) {
      var band = kids.filter(function (e) { return e.tagName !== 'text' && e.tagName !== 'line'; }).map(bbox)
        .filter(function (b) { return b && b.width > 0 && b.y >= top - 6 && b.y < top + 200; });
      var minX = Math.min.apply(null, band.map(function (b) { return b.x; }));
      var maxX = Math.max.apply(null, band.map(function (b) { return b.x + b.width; }));
      if (lay.cfg.remL && minX < posts[0].x - 2) cols.unshift({ Lb: minX, Rb: posts[0].x });
      var lp = posts[posts.length - 1];
      if (cols.length < comps.length && lay.cfg.remR && maxX > lp.x + 10) cols.push({ Lb: lp.x + 8, Rb: maxX });
    }
    if (cols.length !== comps.length) { warn('columns ' + cols.length + ' vs ' + comps.length); return false; }
    comps.forEach(function (c, ci) {
      if (c.t !== 'gate') return;
      var col = cols[ci], H = 0, first = null;
      // 2026-10-09 (Pete: "are you seeing the huge gates on the low fence?"): the gate's top used to be the
      // top of the TALLEST post in the drawing - with a rake that is the raked post (~1600), so both gates
      // were drawn ~1.3x the fence. A gate starts at the top of its OWN posts; of the two, the shorter one
      // (a gate beside the rake has the raked post on one side).
      var gTop = top;
      var own = posts.filter(function (p) { return Math.abs(p.x + 8 - col.Lb) < 1.5 || Math.abs(p.x - col.Rb) < 1.5; });
      if (own.length) gTop = Math.max.apply(null, own.map(function (p) { return p.y; }));
      else { var ys = posts.map(function (p) { return p.y; }).sort(function (x, y) { return x - y; }); gTop = ys[Math.floor(ys.length / 2)]; }
      kids.forEach(function (e) {
        if (e.tagName === 'text' || !e.parentNode) return;
        var b = bbox(e); if (!b) return;
        if (b.x >= col.Lb - 0.6 && b.x + b.width <= col.Rb + 0.6 && b.y < top + 1000 && b.width > 0) {
          if (b.y + b.height - top > H && b.height > 20) H = b.y + b.height - top;
          if (!first) first = e;
          e.parentNode.removeChild(e);
        }
      });
      if (!H) H = posts[0].h * 0.81;
      H = Math.max(20, H - (gTop - top));
      drawGate(svg, first && first.parentNode ? first : null, col.Lb, col.Rb - col.Lb, gTop, H, !c.hingeLeft);
      var mid = (col.Lb + col.Rb) / 2;
      var lab = texts.filter(function (t) { var tx = parseFloat(t.getAttribute('x')); return tx > col.Lb + 2 && tx < col.Rb - 2 && Math.abs(tx - mid) < (col.Rb - col.Lb) / 2; })
        .sort(function (a, b) { return parseFloat(a.getAttribute('y')) - parseFloat(b.getAttribute('y')); });
      if (lab[0]) lab[0].textContent = String(c.w);
      if (lab[1]) lab[1].textContent = 'Gate ' + c.num;
      else if (lab[0]) { var t2 = lab[0].cloneNode(false); t2.setAttribute('y', parseFloat(lab[0].getAttribute('y')) + 12); t2.textContent = 'Gate ' + c.num; svg.appendChild(t2); }
      gatesDone++;
    });
    return gatesDone === 2;
  }

  function fixRendered(gc, n, s, lay) {
    var rp = s.renderedPanels;
    if (!Array.isArray(rp)) return;
    var k = 0, out = [];
    rp.forEach(function (p) {
      if (!p || p.isRaked || p.type === 'hinge' || /rake/i.test(p.type || '')) { out.push(p); return; }
      var it = lay.seq[k++];
      if (it && it.t === 'gate') {
        out.push({ type: 'gate', width: it.w, isRaked: false, isGate: true, retainedHeight: null,
                   gateNum: it.num, cfGate: it.num, cfHingeRight: !it.hingeLeft,
                   hingeSide: it.hingeLeft ? 'left' : 'right', gateSide: gateSideFor(it.hingeLeft) });
      } else out.push(p);
    });
    s.renderedPanels = out;
  }

  var oRSP = P.renderSectionPreview;
  P.renderSectionPreview = function (sectionNum) {
    var s = this.state && this.state.sections && this.state.sections[sectionNum];
    if (!isCustom(s)) return oRSP.apply(this, arguments);
    var lay;
    try { lay = layoutFor(this, s); } catch (e) { warn('layout', e); }
    if (!lay || !lay.seq) return oRSP.apply(this, arguments);
    var oc = this.state.optimizedConfigs || {}, prev = oc[sectionNum];
    var save = { hasGate: s.hasGate, off: s.gateOffsetMm };
    s.hasGate = false; s.gateOffsetMm = 0;
    if (this.state.optimizedConfigs) delete this.state.optimizedConfigs[sectionNum];
    RENDER = { n: num(sectionNum), lay: lay };
    var r;
    try { r = oRSP.apply(this, arguments); }
    finally {
      RENDER = null;
      s.hasGate = save.hasGate; s.gateOffsetMm = save.off;
      if (this.state.optimizedConfigs && prev) this.state.optimizedConfigs[sectionNum] = prev;
    }
    try { fixRendered(this, sectionNum, s, lay); } catch (e) { warn('rendered', e); }
    try { repaint(this, sectionNum, lay); } catch (e) { warn('repaint', e); }
    return r;
  };

  /* ------------------------------------------------------------ the controls (approved wording) */
  function el(html) { var d = document.createElement('div'); d.innerHTML = html.trim(); return d.firstChild; }
  var LBL = 'font-size:14px;font-weight:600;color:#374151;display:block;margin-bottom:8px;';
  var SEL = 'font-size:14px;';
  var RADIO = 'width:16px;height:16px;accent-color:#0e9ed5;cursor:pointer;';
  var BTN = 'flex:1;padding:8px 12px;background:#0e9ed5;color:white;border:none;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;';

  function placeRow(n, g) {
    return '' +
      '<div class="form-group" style="margin-bottom:12px;">' +
        '<label style="' + LBL + '" id="section-' + n + '-a2g' + g + '-place-label">Gate ' + g + ' position</label>' +
        '<select class="form-control" id="section-' + n + '-a2g' + g + '-place" style="' + SEL + '">' +
          '<option value="left">At the left end</option>' +
          '<option value="right">At the right end</option>' +
          '<option value="measured">Measured from an end</option>' +
        '</select>' +
        '<div id="section-' + n + '-a2g' + g + '-measure" style="display:none;gap:6px;margin-top:6px;">' +
          '<select id="section-' + n + '-a2g' + g + '-from" class="form-control" style="flex:0 0 auto;min-width:118px;font-size:13px;padding:8px;">' +
            '<option value="left">From Left</option><option value="right">From Right</option></select>' +
          '<input type="number" id="section-' + n + '-a2g' + g + '-mm" inputmode="numeric" placeholder="e.g. 2000" style="flex:1;min-width:0;padding:8px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;">' +
        '</div>' +
        '<div style="font-size:12px;color:#6b7280;margin-top:5px;" id="section-' + n + '-a2g' + g + '-hint"></div>' +
      '</div>' +
      '<div class="form-group" style="margin-bottom:12px;">' +
        '<label style="' + LBL + '">Gate ' + g + ' Placing</label>' +
        '<div style="display:flex;gap:8px;align-items:center;">' +
          '<button type="button" id="section-' + n + '-a2g' + g + '-mv-left" style="' + BTN + '">&#8592; Move</button>' +
          '<button type="button" id="section-' + n + '-a2g' + g + '-mv-middle" style="' + BTN + '">Middle</button>' +
          '<button type="button" id="section-' + n + '-a2g' + g + '-mv-right" style="' + BTN + '">Move &#8594;</button>' +
        '</div>' +
      '</div>';
  }

  function buildBlock(gc, n) {
    var lengths = '';
    try { lengths = gc.generateGateLengthOptions('soft-close'); } catch (e) {}
    if (!lengths) lengths = '<option value="970">970mm</option>';
    return el('' +
      '<div id="section-' + n + '-a2g" class="cf2g" style="margin-top:4px;">' +
        '<div class="form-group">' +
          '<label class="toggle-label" style="display:flex;align-items:center;justify-content:space-between;">' +
            '<span>Second gate in this section?</span>' +
            '<div class="toggle-switch"><input type="checkbox" id="section-' + n + '-a2g-on"><span class="slider"></span></div>' +
          '</label>' +
        '</div>' +
        '<div id="section-' + n + '-a2g1-card" style="display:none;margin-bottom:8px;">' + placeRow(n, 1) + '</div>' +
        '<div id="section-' + n + '-a2g2-card" style="display:none;border-top:1px solid #e5e7eb;padding-top:12px;">' +
          '<div class="form-group" style="margin-bottom:12px;">' +
            '<label style="' + LBL + '">Gate 2 Length</label>' +
            '<select class="form-control" id="section-' + n + '-a2g2-length" style="' + SEL + '">' + lengths + '</select>' +
          '</div>' +
          '<div class="form-group" style="margin-bottom:12px;">' +
            '<label style="' + LBL + '">Gate 2 Hinge Side</label>' +
            '<div style="display:flex;gap:24px;align-items:center;">' +
              '<label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;color:#374151;"><input type="radio" name="section-' + n + '-a2g2-side" id="section-' + n + '-a2g2-side-left" value="left" style="' + RADIO + '"> LHS Hinge</label>' +
              '<label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;color:#374151;"><input type="radio" name="section-' + n + '-a2g2-side" id="section-' + n + '-a2g2-side-right" value="right" checked style="' + RADIO + '"> RHS Hinge</label>' +
            '</div>' +
          '</div>' +
          placeRow(n, 2) +
        '</div>' +
        '<div id="section-' + n + '-a2g-note" style="font-size:12px;color:#6b7280;margin-top:4px;"></div>' +
      '</div>');
  }

  // The single gate's own placing controls (Move buttons, exact position) step aside while two gates are on.
  function singlePlacing(n) {
    var out = [];
    var b = document.getElementById('section-' + n + '-gate-move-left');
    if (b && b.closest('.form-group')) out.push(b.closest('.form-group'));
    var x = document.getElementById('cf-gate-exact-' + n);
    if (x) out.push(x);
    var ex = document.getElementById('section-' + n + '-gate-exact');
    if (ex && ex.closest('.form-group')) out.push(ex.closest('.form-group'));
    return out;
  }

  function noteText(lay) {
    if (!lay) return '';
    if (lay.error === 'same-end') return 'Both gates are set to the same end. Put one gate at each end, or measure one of them from an end.';
    if (lay.error === 'rake-end') return 'The raked panel is at that end, so a gate cannot go there. Choose the other end, or measure the gate from an end.';
    if (lay.error && lay.compact && lay.seq && lay.seq.some(function (it) { return it.t === 'gate' && it.slot > it.w; })) return '';
    if (lay.error) return 'The gates fit in this section, just not where they are now. Move a gate (or measure it from the other end) and it will fit.';
    return (lay.moved || []).map(function (m) { return 'Gate ' + m.num + ' moved ' + Math.abs(m.shift) + 'mm so whole panels fit.'; }).join(' ');
  }

  function syncUi(gc, n) {
    var s = gc.state.sections[n];
    var box = document.getElementById('section-' + n + '-a2g');
    if (!s || !box) return;
    box.style.display = s.hasGate ? '' : 'none';
    var q = function (id) { return document.getElementById('section-' + n + '-' + id); };
    var on = !!(s.hasGate && s.hasGate2);
    var t = q('a2g-on'); if (t) t.checked = on;
    q('a2g1-card').style.display = on ? '' : 'none';
    q('a2g2-card').style.display = on ? '' : 'none';
    singlePlacing(n).forEach(function (e) { e.style.display = on ? 'none' : ''; });
    var note = q('a2g-note');
    if (!on) { if (note) note.textContent = ''; return; }
    [1, 2].forEach(function (g) {
      var pre = 'cfG' + g;
      var place = s[pre + 'Place'] || (g === 1 ? 'left' : 'right');
      var pl = q('a2g' + g + '-place'); if (pl) pl.value = place;
      var ms = q('a2g' + g + '-measure'); if (ms) ms.style.display = place === 'measured' ? 'flex' : 'none';
      var fr = q('a2g' + g + '-from'); if (fr) fr.value = s[pre + 'From'] || (g === 1 ? 'left' : 'right');
      var mm = q('a2g' + g + '-mm'); if (mm && document.activeElement !== mm) mm.value = s[pre + 'Mm'] != null ? s[pre + 'Mm'] : '';
      var h = q('a2g' + g + '-hint'); if (h) h.textContent = place === 'measured' ? 'Distance from the chosen end to the near edge of the gate.' : '';
    });
    var len = q('a2g2-length'); if (len) len.value = String(num(s.cfG2Length) || num(s.gateLength));
    var sideL = s.cfG2Side === 'left';
    var rl = q('a2g2-side-left'), rr = q('a2g2-side-right');
    if (rl) rl.checked = sideL; if (rr) rr.checked = !sideL;
    var lay = s.__cfA2gLay;
    if (note) {
      if (lay && lay.tooShort) { note.style.color = '#b45309'; note.textContent = 'This is the shortest section that fits these gates: ' + lay.minLength + 'mm.'; }
      else { var tx = noteText(lay); note.style.color = lay && lay.error ? '#b45309' : '#6b7280'; note.textContent = tx; }
    }
  }

  function recalc(gc, n) {
    if (gc.state.optimizedConfigs) delete gc.state.optimizedConfigs[n];
    try { gc.updateCalculationResults(n); } catch (e) { warn('recalc', e); }
    try { gc.renderSectionPreview(n); } catch (e) {}
    try { gc.trigger3DUpdate(); } catch (e) {}
    try { gc.updateQuoteDisplay(); } catch (e) {}
    try { gc.updateComponentsQuote(); } catch (e) {}
    try { syncUi(gc, n); } catch (e) {}
  }

  // Where the single gate is drawn now, so turning on gate 2 does not move gate 1.
  function currentPlacement(gc, n, s) {
    try {
      var rp = (s.renderedPanels || []).filter(function (p) { return p && p.type !== 'hinge'; });
      var Pw = num(s.gapSpacing) || 50, x = Pw, gi = -1;
      for (var i = 0; i < rp.length; i++) { if (rp[i].type === 'gate') { gi = i; break; } x += num(rp[i].width) + Pw; }
      if (gi === 0) return { place: 'left' };
      if (gi === rp.length - 1) return { place: 'right' };
      var L = num(s.length), w = num(s.gateLength);
      if (gi > 0) return x < L / 2 ? { place: 'measured', from: 'left', mm: x } : { place: 'measured', from: 'right', mm: Math.max(0, L - x - w) };
    } catch (e) {}
    return { place: 'left' };
  }

  // Leaf start of each item in the solved layout, in the all-posts frame.
  function positions(lay) {
    var c = lay.cfg, x = (c.rake && c.rake.side === 'left' ? c.P + c.rake.w : 0) + c.P, out = [];
    lay.seq.forEach(function (it) { out.push(x); x += (it.t === 'gate' ? it.slot : it.w) + c.P; });
    return out;
  }
  function moveGate(gc, s, g, dir) {
    var lay = layoutFor(gc, s);
    if (!lay || !lay.seq) return false;
    var c = lay.cfg, pre = 'cfG' + g, w = g === 1 ? c.gates[0].w : c.gates[1].w;
    if (dir === 'middle') {
      s[pre + 'Place'] = 'measured'; s[pre + 'From'] = 'left'; s[pre + 'Mm'] = Math.max(0, Math.round((c.L - w) / 2));
      return true;
    }
    var xs = positions(lay), i = -1;
    lay.seq.forEach(function (it, k) { if (it.t === 'gate' && it.num === g) i = k; });
    if (i < 0) return false;
    var j = dir === 'left' ? i - 1 : i + 1;
    var nb = lay.seq[j];
    if (!nb || nb.t !== 'panel') return false;            // already at the end, or against the other gate
    var x = dir === 'left' ? xs[i] - (nb.w + c.P) : xs[i] + (nb.w + c.P);
    var atLeft = dir === 'left' && j === 0, atRight = dir === 'right' && j === lay.seq.length - 1;
    if (atLeft) s[pre + 'Place'] = 'left';
    else if (atRight) s[pre + 'Place'] = 'right';
    else { s[pre + 'Place'] = 'measured'; s[pre + 'From'] = 'left'; s[pre + 'Mm'] = Math.max(0, x - c.remL * c.P); }
    return true;
  }

  // Gate 2 off: the customer's own end post choice comes back.
  function restoreEp(gc, s) {
    if (s.cfEpChosen && s.cfEpApplied && s.endPostSetup === s.cfEpApplied) setEp(gc, s, s.cfEpChosen);
    s.cfEpChosen = null; s.cfEpApplied = null;
  }

  function tryOn(gc, n, s) {
    var p = currentPlacement(gc, n, s);
    s.cfG1Place = p.place; s.cfG1From = p.from || 'left'; if (p.place === 'measured') s.cfG1Mm = Math.round(p.mm);
    var g1Left = p.place === 'left' || (p.place === 'measured' && p.from === 'left');
    s.cfG2Place = g1Left ? 'right' : 'left';
    s.cfG2From = s.cfG2Place;
    s.cfG2Side = g1Left ? 'right' : 'left';
    s.cfG2Length = String(num(s.cfG2Length) || num(s.gateLength));
    s.hasGate2 = true;
    s.cfLastMoved = 0;
    var lay = layoutFor(gc, s);
    if (lay.error && !lay.tooShort) {   // not where they are: one gate at each end
      s.cfG1Place = g1Left ? 'left' : 'right'; lay = layoutFor(gc, s);
    }
    if (lay.tooShort) {
      s.hasGate2 = false;
      try {
        gc.showValidationPopup('Two gates need a longer section - at least ' + lay.minLength + 'mm with these gates.' +
          '\n\nMake this section longer, or send us a drawing on the "Send us your plans" page and we will advise.');
      } catch (x) {}
      return false;
    }
    return true;
  }

  function wire(gc, n, box) {
    var q = function (id) { return box.querySelector('#section-' + n + '-' + id); };
    var S = function () { return gc.state.sections[n]; };
    q('a2g-on').addEventListener('change', function (e) {
      var s = S(); if (!s) return;
      if (e.target.checked) { if (!tryOn(gc, n, s)) e.target.checked = false; }
      else { s.hasGate2 = false; restoreEp(gc, s); }
      recalc(gc, n);
    });
    [1, 2].forEach(function (g) {
      var pre = 'cfG' + g;
      q('a2g' + g + '-place').addEventListener('change', function (e) {
        var s = S(); if (!s) return;
        s[pre + 'Place'] = e.target.value; s.cfLastMoved = g;
        if (e.target.value === 'measured' && (s[pre + 'Mm'] == null || s[pre + 'Mm'] === '')) {
          s[pre + 'From'] = s[pre + 'From'] || 'left';
          s[pre + 'Mm'] = Math.round((num(s.length) - (num(g === 1 ? s.gateLength : s.cfG2Length) || 970)) / 2);
        }
        recalc(gc, n);
      });
      q('a2g' + g + '-from').addEventListener('change', function (e) { var s = S(); if (!s) return; s[pre + 'From'] = e.target.value; recalc(gc, n); });
      var mm = q('a2g' + g + '-mm');
      var commit = function () { var s = S(); if (!s) return; var v = Math.max(0, num(mm.value)); mm.value = v; s.cfLastMoved = g; if (String(s[pre + 'Mm']) === String(v)) return; s[pre + 'Mm'] = v; recalc(gc, n); };
      mm.addEventListener('change', commit);
      mm.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); commit(); } });
    });
    // The calculator's own Move buttons, one row per gate: a gate jumps one panel along, or to the middle.
    [1, 2].forEach(function (g) {
      ['left', 'middle', 'right'].forEach(function (dir) {
        q('a2g' + g + '-mv-' + dir).addEventListener('click', function () {
          var s = S(); if (!s) return;
          try { s.cfLastMoved = g; if (moveGate(gc, s, g, dir)) recalc(gc, n); } catch (e) { warn('move', e); }
        });
      });
    });
    q('a2g2-length').addEventListener('change', function (e) { var s = S(); if (!s) return; s.cfG2Length = e.target.value; recalc(gc, n); });
    ['left', 'right'].forEach(function (side) {
      q('a2g2-side-' + side).addEventListener('change', function (e) { var s = S(); if (!s || !e.target.checked) return; s.cfG2Side = side; recalc(gc, n); });
    });
  }

  var oForm = P.createGateConfigForm;
  if (typeof oForm === 'function') {
    P.createGateConfigForm = function (sectionNum) {
      var form = oForm.apply(this, arguments);
      try {
        if (form && form.appendChild) {
          var box = buildBlock(this, sectionNum);
          form.appendChild(box);
          wire(this, sectionNum, box);
          var gc = this;
          setTimeout(function () { try { syncUi(gc, sectionNum); } catch (e) {} }, 0);
        }
      } catch (e) { warn('controls', e); }
      return form;
    };
  }

  var oToggle = P.toggleGate;
  if (typeof oToggle === 'function') {
    P.toggleGate = function (sectionNum, checked) {
      var s = this.state && this.state.sections && this.state.sections[sectionNum];
      if (!checked && s) { s.hasGate2 = false; restoreEp(this, s); }
      var r = oToggle.apply(this, arguments);
      var gc = this;
      setTimeout(function () { try { syncUi(gc, sectionNum); } catch (e) {} }, 0);
      return r;
    };
  }

  // A section shortened below what two gates need is lengthened to the shortest that fits, with a
  // note - the single-gate rule (Pete, 30 Aug). Never while the customer is still typing.
  function maybeSnap(gc, n, s) {
    var lay = s.__cfA2gLay;
    if (!lay || !lay.tooShort) return;
    var input = document.getElementById('section-' + n + '-length');
    if (input && document.activeElement === input) {
      if (!input.__a2gBlur) {
        input.__a2gBlur = true;
        input.addEventListener('blur', function () { setTimeout(function () { var sec = gc.state.sections[n]; if (isCustom(sec)) { layoutFor(gc, sec); maybeSnap(gc, n, sec); } }, 50); });
      }
      return;
    }
    var minL = lay.minLength;
    try { if (gc.updateSectionDataSilently) gc.updateSectionDataSilently(n, 'length', String(minL)); } catch (e) {}
    s.length = String(minL);
    if (input) input.value = minL;
    recalc(gc, n);
    var note = document.getElementById('section-' + n + '-a2g-note');
    if (note) { note.style.color = '#b45309'; note.textContent = 'This is the shortest section that fits these gates: ' + minL + 'mm.'; }
  }

  if (typeof P._recalcAndRender === 'function') {
    var oRR = P._recalcAndRender;
    P._recalcAndRender = function (sectionNum) {
      var gc = this, a = arguments;
      return withCur(this.state && this.state.sections && this.state.sections[sectionNum], function () { return oRR.apply(gc, a); });
    };
  }
  var oUCR = P.updateCalculationResults;
  if (typeof oUCR === 'function') {
    P.updateCalculationResults = function (sectionNum) {
      var gc0 = this, a0 = arguments;
      var r = withCur(this.state && this.state.sections && this.state.sections[sectionNum], function () { return oUCR.apply(gc0, a0); });
      try {
        var s = this.state.sections[sectionNum];
        if (isCustom(s)) { var lay = layoutFor(this, s); var gc = this; if (lay.tooShort) setTimeout(function () { maybeSnap(gc, sectionNum, s); }, 0); syncUi(this, sectionNum); }
      } catch (e) {}
      return r;
    };
  }

  // Some patches wrap calculateSpread on the calculator instance (outside this prototype hook) and
  // re-fit the result for ONE gate, stretching a panel by gate 2 + its post. Keep a final layer on
  // the instance too, re-applied whenever another patch wraps it again.
  function outerWrap() {
    var gc = window.glassCalculator;
    if (!gc || typeof gc.calculateSpread !== 'function' || gc.calculateSpread.__a2gOuter) return;
    var inner = gc.calculateSpread;
    var fn = function (len, pw, sheet, element, isW2P, wallGateEnd, gateLeaf) {
      var r = inner.apply(this, arguments);
      if (BYPASS) return r;
      try {
        var s = sectionForSpread(this, len, element, gateLeaf);
        if (s && r) { var lay = layoutFor(this, s); if (lay.seq) return realCalc(r, lay); }
      } catch (e) { warn('spread outer', e); }
      return r;
    };
    fn.__a2gOuter = true;
    gc.calculateSpread = fn;
  }
  function outerWrapPlan() {
    var gc = window.glassCalculator;
    if (!gc || typeof gc.calculateJobCutPlan !== 'function' || gc.calculateJobCutPlan.__a2gOuter) return;
    var inner = gc.calculateJobCutPlan;
    var fn = function () { return fixPlan(this, inner.apply(this, arguments)); };
    fn.__a2gOuter = true;
    gc.calculateJobCutPlan = fn;
  }
  var tries = 0;
  var tick = setInterval(function () { try { outerWrap(); outerWrapPlan(); } catch (e) {} if (++tries > 60) clearInterval(tick); }, 500);
  document.addEventListener('click', function () { try { outerWrap(); outerWrapPlan(); } catch (e) {} }, true);

  // The saved quote (quote page, PDF, CRM): each two-gate section lists its gates, the same fields
  // the glass two-gate sections send, so the quote page counts and labels Gate 1 / Gate 2.
  function stampPayload(gc) {
    var secs = gc.state.sections || {}, nums = Object.keys(secs).map(Number).sort(function (a, b) { return a - b; });
    if (!nums.some(function (n) { return isCustom(secs[n]); })) return;
    var enrich = function (list) {
      (list || []).forEach(function (ps, i) {
        var n = ps && ps.num != null ? num(ps.num) : nums.filter(function (k) { return num(secs[k].length) > 0; })[i];
        var s = secs[n];
        if (!ps || !isCustom(s)) return;
        var lay = s.__cfA2gLay;
        if (!lay || !lay.seq) return;
        ps.hasGate2 = true;
        ps.gates = lay.seq.filter(function (it) { return it.t === 'gate'; })
          .map(function (it) { return { num: it.num, width: it.w, hinge: it.hingeLeft ? 'left' : 'right', gateSide: gateSideFor(it.hingeLeft) }; })
          .sort(function (a, b) { return a.num - b.num; });
        var rp = (s.renderedPanels || []).filter(function (p) { return p && p.type !== 'hinge'; });
        if (Array.isArray(ps.panels) && ps.panels.length === rp.length) {
          ps.panels.forEach(function (p, k) { var r = rp[k]; if (p && r && r.type === 'gate') { p.type = 'gate'; p.cfGate = r.cfGate; p.cfHingeRight = r.cfHingeRight; p.gateSide = r.gateSide; p.hingeSide = r.hingeSide; } });
        }
      });
    };
    try { var raw = sessionStorage.getItem('ullc_quote'); if (raw) { var q = JSON.parse(raw); enrich(q.sections); sessionStorage.setItem('ullc_quote', JSON.stringify(q)); } } catch (e) { warn('stamp', e); }
    var key = KIND === 'blade' ? 'blade_quotes' : KIND === 'flattop' ? 'flattop_quotes' : 'perfmesh_quotes';
    try {
      var lst = JSON.parse(localStorage.getItem(key) || '[]');
      if (Array.isArray(lst) && lst.length) { enrich(lst[lst.length - 1].sections); localStorage.setItem(key, JSON.stringify(lst)); }
    } catch (e) { warn('stamp list', e); }
  }
  ['proceedToQuotePage', 'addToCart'].forEach(function (m) {
    var o = P[m];
    if (typeof o !== 'function') return;
    P[m] = function () {
      var out = o.apply(this, arguments);
      var gc = this;
      try { stampPayload(gc); } catch (e) { warn('payload', e); }
      if (out && typeof out.then === 'function') out.then(function () { try { stampPayload(gc); } catch (e) {} });
      return out;
    };
  });

  P.__cfAluTwoGates = true;
  try { console.log(TAG + ' installed on ' + KIND); } catch (e) {}
})();
